<template>
  <div class="simple-train-row" data-testid="simple-train-row">
    <v-row dense>
      <v-col cols="12" md="4">
        <v-card variant="outlined" class="pa-4 h-100">
          <div class="text-subtitle-1 mb-2">{{ $t('simple_mode.train_title') }}</div>
          <div v-if="latestModel" class="d-flex align-center mb-3">
            <span class="text-body-2 mr-2">{{ $t('simple_mode.last_performance') }}</span>
            <model-performance-chip :model="latestModel" :threshold="50" />
          </div>
          <div v-else class="text-body-2 text-medium-emphasis mb-3">
            {{ $t('simple_mode.no_model_yet') }}
          </div>
          <p class="text-body-2 text-medium-emphasis mb-2">
            {{ $t('simple_mode.train_hint') }}
          </p>
          <p
            v-if="validatedCount < MIN_VALIDATED_FOR_TRAIN"
            class="text-caption mb-3"
            data-testid="simple-train-need-more"
          >
            {{ $t('simple_mode.train_need_more', { count: validatedCount, min: MIN_VALIDATED_FOR_TRAIN }) }}
          </p>
          <v-btn
            color="primary"
            :loading="training"
            :disabled="!canTrain"
            data-testid="simple-train-btn"
            @click="startTrain"
          >
            {{ $t('simple_mode.train_button') }}
          </v-btn>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card variant="outlined" class="pa-4 h-100">
          <div class="text-subtitle-1 mb-2">{{ $t('simple_mode.browser_run_title') }}</div>
          <p class="text-body-2 text-medium-emphasis mb-3">
            {{ $t('simple_mode.browser_run_hint') }}
          </p>
          <v-btn
            color="primary"
            variant="outlined"
            :disabled="!browserModel"
            :loading="browserRunning"
            data-testid="simple-browser-run-btn"
            @click="runInBrowser"
          >
            {{ $t('simple_mode.browser_run_button') }}
          </v-btn>
          <div v-if="browserStatus" class="text-caption mt-2" data-testid="simple-browser-status">
            {{ browserStatus }}
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card variant="outlined" class="pa-4 h-100">
          <div class="text-subtitle-1 mb-2">{{ $t('simple_mode.apply_all_title') }}</div>
          <p class="text-body-2 text-medium-emphasis mb-3">
            {{ $t('simple_mode.apply_all_hint') }}
          </p>
          <v-btn
            color="primary"
            variant="outlined"
            :disabled="!applyModel"
            :loading="applying"
            data-testid="simple-apply-all-btn"
            @click="applyToUnvalidated"
          >
            {{ $t('simple_mode.apply_all_button') }}
          </v-btn>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ModelPerformanceChip from '@/components/models/ModelPerformanceChip.vue';
import LoadModelsMixin from '@/mixins/LoadModelsMixin';
import {
  modelHasBrowserTflite,
  runAnnotateLiteRtInference,
} from '@/utils/annotateLiteRtInference';
import { buildAssetStateFromNextResult } from '@/utils/annotatePreload';
import Annotation from '@/classes/annotation';

const UNVALIDATED_FILTER = [{ type: 9, value: 'No' }];
const VALIDATED_COUNT_FILTER = [{ type: 9, value: 'Yes' }, { type: 61, value: 'No' }];
const MIN_VALIDATED_FOR_TRAIN = 200;

export default {
  name: 'SimpleTrainRow',
  components: { ModelPerformanceChip },
  mixins: [LoadModelsMixin],
  data() {
    return {
      trainConfigs: [],
      training: false,
      applying: false,
      browserRunning: false,
      browserStatus: '',
      currentAssetState: null,
      validatedCount: 0,
    };
  },
  computed: {
    MIN_VALIDATED_FOR_TRAIN() {
      return MIN_VALIDATED_FOR_TRAIN;
    },
    latestModel() {
      const items = (this.models || [])
        .filter((m) => !m.archived)
        .slice()
        .sort((a, b) => (b.id || 0) - (a.id || 0));
      return items[0] || null;
    },
    applyModel() {
      const active = (this.models || []).find((m) => m.active && !m.archived);
      return active || this.latestModel;
    },
    browserModel() {
      const withTflite = (this.models || []).find(
        (m) => !m.archived && modelHasBrowserTflite(m),
      );
      return withTflite || null;
    },
    preferredTrainConfig() {
      return (this.trainConfigs || []).find((tc) => !tc.archived) || null;
    },
    canTrain() {
      return this.validatedCount >= MIN_VALIDATED_FOR_TRAIN;
    },
  },
  async mounted() {
    await this.reloadTrainConfigs();
    await this.prefetchAsset();
    await this.refreshValidatedCount();
  },
  methods: {
    async refreshValidatedCount() {
      try {
        const count = await this.$api.asset.count_filter(VALIDATED_COUNT_FILTER);
        this.validatedCount = typeof count === 'number' ? count : 0;
      } catch {
        this.validatedCount = 0;
      }
    },
    async reloadTrainConfigs() {
      try {
        await this.$store.dispatch('train-config/all');
        this.trainConfigs = this.$store.getters['train-config/get_items'] || [];
      } catch {
        const res = await this.$api.train_config.all(
          { page: 1, itemsPerPage: 50 },
          '',
          undefined,
          false,
        );
        this.trainConfigs = res?.items || (Array.isArray(res) ? res : []);
      }
    },
    async ensureTrainConfig() {
      if (this.preferredTrainConfig) {
        return this.preferredTrainConfig;
      }
      const result = await this.$api.onboarding.bootstrapTrainConfig();
      if (result?.error) {
        const key = `models_onboarding.error_${result.error}`;
        const msg = this.$te(key) ? this.$t(key) : result.error;
        this.$store.dispatch('snackbars/add_failed_snack', msg);
        return null;
      }
      await this.reloadTrainConfigs();
      return this.preferredTrainConfig;
    },
    async startTrain() {
      this.training = true;
      try {
        const tc = await this.ensureTrainConfig();
        if (!tc) {
          return;
        }
        await this.$api.job.start_train_job(tc.id);
        this.$store.dispatch('snackbars/add_success_snack', this.$t('simple_mode.train_started'));
        this.$emit('train-started');
      } catch (e) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('simple_mode.train_failed'));
      } finally {
        this.training = false;
      }
    },
    async prefetchAsset() {
      const filters = JSON.stringify(UNVALIDATED_FILTER);
      const result = await this.$api.asset.next_asset(filters);
      if (!result || result.done || result.error || !result.asset) {
        this.currentAssetState = null;
        return;
      }
      this.currentAssetState = buildAssetStateFromNextResult(
        result,
        this.$api_host,
        Annotation,
      );
    },
    async runInBrowser() {
      if (!this.browserModel) {
        return;
      }
      this.browserRunning = true;
      this.browserStatus = this.$t('simple_mode.browser_running');
      try {
        if (!this.currentAssetState) {
          await this.prefetchAsset();
        }
        if (!this.currentAssetState) {
          this.browserStatus = this.$t('simple_mode.browser_no_asset');
          return;
        }
        const projectId = sessionStorage.getItem('project_id');
        const preds = await runAnnotateLiteRtInference({
          model: this.browserModel,
          assetImageUrl: this.currentAssetState.asset_url,
          projectId,
          token: localStorage.token,
        });
        const count = Array.isArray(preds?.annotations) ? preds.annotations.length : 0;
        this.browserStatus = this.$t('simple_mode.browser_done', { count });
        this.$emit('browser-run', preds);
      } catch (e) {
        this.browserStatus = this.$t('simple_mode.browser_failed');
      } finally {
        this.browserRunning = false;
      }
    },
    async applyToUnvalidated() {
      if (!this.applyModel) {
        return;
      }
      this.applying = true;
      try {
        await this.$api.asset.analyse_with_filter({
          model: this.applyModel,
          filter: UNVALIDATED_FILTER,
        });
        this.$store.dispatch('snackbars/add_success_snack', this.$t('simple_mode.apply_queued'));
        this.$emit('apply-started');
      } catch (e) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('simple_mode.apply_failed'));
      } finally {
        this.applying = false;
      }
    },
  },
};
</script>
